/**
 * 🛡️ Admin Protected Route
 *
 * يحمي كل صفحات /admin/* — يفحص الـ role قبل ما يحمّل أي صفحة.
 *
 * 🚨 Security Note (H-2 fix):
 *   كان الـ ProtectedRoute العام يفحص isAuthenticated فقط.
 *   الـ guards الموجودة كانت في كل صفحة (يدوياً)، لكن:
 *     - كل صفحات الأدمن تستخدم هذا الـ guard
 *     - الـ component يحمّل + يقرأ data قبل ما يفحص الـ role
 *     - user عادي يقدر يفتح /admin/cleanup بـ URL مباشر
 *       ويشوف data تجار آخرين قبل ما assertAdmin يرفض
 *
 *   الآن: AdminProtectedRoute يحوّل user عادي قبل ما الـ component يحمّل.
 *
 * Usage في router.tsx:
 *   {
 *     element: <AdminProtectedRoute />,
 *     children: [
 *       { path: 'admin/cities', element: <AdminCities /> },
 *       // ...
 *     ]
 *   }
 */

import { Navigate, Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldAlert } from 'lucide-react';

import { useAuthStore } from '@application/stores/auth.store';

/**
 * Roles المسموح لها بدخول /admin/*
 */
const ADMIN_ROLES = ['admin', 'super_admin'] as const;

export function AdminProtectedRoute() {
  const { user, isAuthenticated, isLoading, isHydrated } = useAuthStore();

  // أثناء التحقق من session — نخلي ProtectedRoute العام يهتم
  if (!isHydrated || isLoading) {
    return null; // ProtectedRoute راح يعرض spinner
  }

  // غير مسجّل دخول — ProtectedRoute العام يحوّله لـ login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // 🛡️ الفحص الأساسي: لازم يكون admin
  const isAdmin = ADMIN_ROLES.includes(user.role as (typeof ADMIN_ROLES)[number]);

  if (!isAdmin) {
    // 🚫 ليس admin — يطلع رسالة واضحة بدلاً من redirect صامت
    return <AccessDeniedScreen />;
  }

  return <Outlet />;
}

// ═══════════════════════════════════════════════════
// 🚫 Access Denied UI (موحّد لكل الـ admin pages)
// ═══════════════════════════════════════════════════
function AccessDeniedScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-amber-50 p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl shadow-2xl border-2 border-rose-200 p-8 max-w-md w-full text-center"
      >
        <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-rose-100 mb-5">
          <ShieldAlert className="h-10 w-10 text-rose-600" />
        </div>

        <h1 className="text-2xl font-bold text-rose-700 mb-2">
          صلاحيات غير كافية
        </h1>

        <p className="text-sm text-rose-600 mb-6 leading-relaxed">
          هذه الصفحة للأدمن فقط. لا تملك الصلاحيات اللازمة للوصول إليها.
        </p>

        <a
          href="/"
          className="inline-block px-6 py-2.5 rounded-xl bg-gradient-to-r from-brand-purple-600 to-brand-purple-700 text-white text-sm font-bold shadow-lg shadow-brand-purple-200/40 hover:shadow-xl hover:-translate-y-0.5 transition-all"
        >
          العودة للرئيسية
        </a>
      </motion.div>
    </div>
  );
}
